import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Loader2, Upload, Trash2, Check, Images, GalleryHorizontal } from "lucide-react";

/**
 * Admin — nahrávání fotek do galerie na hlavní stránce (před/po páry) a do
 * samostatné fotogalerie (/fotogalerie). Soubory jdou přes Emergent Object
 * Storage (POST multipart), veřejné GET endpointy je pak čtou přes
 * /api/gallery/image/{path}.
 */
export default function GalleryManagerPanel() {
  return (
    <div className="space-y-6" data-testid="gallery-manager-panel">
      <div>
        <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
          <Images className="w-5 h-5 text-apex-gold" /> Galerie
        </h3>
        <p className="text-apex-cream/60 text-sm mt-1">
          Správa fotek zobrazených na hlavní stránce (před/po) a v samostatné fotogalerii.
        </p>
      </div>
      <PairsManager />
      <PhotosManager />
    </div>
  );
}

function Msg({ msg }) {
  if (!msg) return null;
  return (
    <div
      className={`mb-4 rounded-lg px-4 py-2 text-sm ${msg.kind === "ok" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"}`}
      data-testid="gallery-manager-msg"
    >
      {msg.kind === "ok" && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
      {msg.text}
    </div>
  );
}

function PairsManager() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [label, setLabel] = useState("");
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/gallery/pairs").then((r) => setPairs(r.data?.pairs || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const addPair = async () => {
    const before = beforeRef.current?.files?.[0];
    const after = afterRef.current?.files?.[0];
    if (!before || !after) {
      setMsg({ kind: "err", text: "Vyberte obě fotky (před i po)." });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append("label", label || "Před a po");
      fd.append("before", before);
      fd.append("after", after);
      await api.post("/gallery/pairs", fd);
      setLabel("");
      if (beforeRef.current) beforeRef.current.value = "";
      if (afterRef.current) afterRef.current.value = "";
      setMsg({ kind: "ok", text: "Pár před/po byl nahrán." });
      load();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Nahrání selhalo." });
    } finally {
      setSaving(false);
    }
  };

  const removePair = async (id) => {
    if (!confirm("Smazat tento pár před/po?")) return;
    await api.delete(`/gallery/pairs/${id}`);
    load();
  };

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="gallery-pairs-section">
      <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-1 flex items-center gap-2">
        <GalleryHorizontal className="w-3.5 h-3.5" /> Před / po slider — hlavní stránka
      </div>
      <p className="text-xs text-apex-cream/60 mb-4">Zobrazuje se v sekci Galerie na hlavní stránce. Nahrajte vždy dvojici fotek.</p>

      <Msg msg={msg} />

      <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end mb-5 p-3 rounded-xl bg-apex-bg/50 border border-apex-line">
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Popisek</div>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="např. Interiér · koberečky" className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="gallery-pair-label-input" />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Před</div>
          <input ref={beforeRef} type="file" accept="image/*" className="text-xs text-apex-cream/80 w-40" data-testid="gallery-pair-before-input" />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Po</div>
          <input ref={afterRef} type="file" accept="image/*" className="text-xs text-apex-cream/80 w-40" data-testid="gallery-pair-after-input" />
        </label>
        <button onClick={addPair} disabled={saving} className="btn-gold !py-2 !px-4 text-sm disabled:opacity-50" data-testid="gallery-pair-upload-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Nahrát
        </button>
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-apex-gold" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gallery-pairs-list">
          {pairs.map((p) => (
            <div key={p.id} className="rounded-xl border border-apex-line overflow-hidden" data-testid={`gallery-pair-row-${p.id}`}>
              <div className="grid grid-cols-2">
                <img src={p.before_url} alt="před" className="w-full h-24 object-cover" />
                <img src={p.after_url} alt="po" className="w-full h-24 object-cover" />
              </div>
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-xs text-apex-cream/80 truncate">{p.label}</span>
                <button onClick={() => removePair(p.id)} className="text-red-300 hover:text-red-200 flex-shrink-0" data-testid={`gallery-pair-delete-${p.id}`} title="Smazat">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotosManager() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/gallery/photos").then((r) => setPhotos(r.data?.photos || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const addPhotos = async () => {
    const files = Array.from(fileRef.current?.files || []);
    if (files.length === 0) {
      setMsg({ kind: "err", text: "Vyberte alespoň jednu fotku." });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("caption", caption || "");
        fd.append("file", file);
        await api.post("/gallery/photos", fd);
      }
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      setMsg({ kind: "ok", text: `Nahráno ${files.length} fotek do fotogalerie.` });
      load();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Nahrání selhalo." });
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = async (id) => {
    if (!confirm("Smazat tuto fotku?")) return;
    await api.delete(`/gallery/photos/${id}`);
    load();
  };

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="gallery-photos-section">
      <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-1 flex items-center gap-2">
        <Images className="w-3.5 h-3.5" /> Samostatná fotogalerie — stránka /fotogalerie
      </div>
      <p className="text-xs text-apex-cream/60 mb-4">Fotky se zobrazí na stránce, na kterou vede tlačítko „Zobrazit celou fotogalerii“ pod před/po sliderem.</p>

      <Msg msg={msg} />

      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end mb-5 p-3 rounded-xl bg-apex-bg/50 border border-apex-line">
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Popisek (volitelný, platí pro všechny vybrané fotky)</div>
          <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="gallery-photo-caption-input" />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Fotky (lze vybrat víc)</div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="text-xs text-apex-cream/80 w-52" data-testid="gallery-photo-file-input" />
        </label>
        <button onClick={addPhotos} disabled={saving} className="btn-gold !py-2 !px-4 text-sm disabled:opacity-50" data-testid="gallery-photo-upload-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Nahrát
        </button>
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-apex-gold" />
      ) : photos.length === 0 ? (
        <div className="text-xs italic text-apex-cream/45">Zatím žádné fotky ve fotogalerii.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="gallery-photos-list">
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden" data-testid={`gallery-photo-row-${p.id}`}>
              <img src={p.url} alt={p.caption || ""} className="w-full h-20 object-cover" />
              <button
                onClick={() => removePhoto(p.id)}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-red-300"
                data-testid={`gallery-photo-delete-${p.id}`}
                title="Smazat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
